import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Card,
  Space,
  Input,
  Select,
  Typography,
  message,
  Row,
  Col,
  Statistic,
  theme,
} from 'antd';
import { PermissionButton } from '../components/common/PermissionButton';
import {
  PlusOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  SettingOutlined,
  ShopOutlined,
  ToolOutlined,
  ShoppingCartOutlined,
  ContactsOutlined,
} from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { SupplierFormModal } from '../components/suppliers/SupplierFormModal';
import { NameLink, ListActionsDropdown, PageHeader, PageContainer } from '../components/common';
import { CategoryTag } from '../components/common/CategoryTag';
import { CategoryTagCloudFilter } from '../components/common/CategoryTagCloudFilter';
import {
  DEFAULT_SUPPLIER_LIST_SORT,
  SUPPLIER_LIST_SORT_STORAGE_KEY,
  getSortOrderLabel,
  loadSortPreference,
  persistSortPreference,
  type SortPreference,
  type SupplierListSortField,
} from './utils/sortPreferences';

const { Title, Text } = Typography;

interface Supplier {
  id: string;
  companyId: string;
  displayId: string;
  name: string;
  nameKana?: string | null;
  postalCode?: string | null;
  address?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  email?: string | null;
  contactPerson?: string | null;
  managerId?: string | null;
  supplierCategoryId?: string | null;
  category?: {
    id: string;
    categoryLabel: string;
    color?: string | null;
    icon?: string | null;
  } | null;
  notes?: string | null;
  createdBy: string;
  updatedBy?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletionReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function SuppliersPage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(() => {
    // URLクエリパラメータから初期値を取得
    return searchParams.get('category') || undefined;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortPreference, setSortPreference] = useState<SortPreference<SupplierListSortField>>(() =>
    loadSortPreference(
      SUPPLIER_LIST_SORT_STORAGE_KEY,
      ['name', 'contactPerson', 'updatedAt'] as const,
      DEFAULT_SUPPLIER_LIST_SORT,
    )
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [, messageContextHolder] = message.useMessage();

  useEffect(() => {
    persistSortPreference(SUPPLIER_LIST_SORT_STORAGE_KEY, sortPreference);
  }, [sortPreference]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCategory, sortPreference]);

  // 業者一覧取得
  const { data: suppliersData, isLoading, refetch } = trpc.suppliers.list.useQuery({
    page: currentPage,
    limit: pageSize,
    search: searchText || undefined,
    category: selectedCategory && selectedCategory.trim() !== '' ? selectedCategory : undefined,
    sort: sortPreference,
  });

  // 業者カテゴリ一覧取得
  const { data: supplierCategories = [] } = trpc.supplierCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const suppliers = useMemo<Supplier[]>(() => {
    const items = suppliersData?.items;
    if (!Array.isArray(items)) {
      return [];
    }
    return items.filter((item): item is Supplier => Boolean(item?.id && item?.name));
  }, [suppliersData]);
  const total = suppliersData?.pagination?.total || 0;

  // 編集モーダル表示
  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setIsModalVisible(true);
  };

  // 新規登録モーダル表示
  const handleCreate = () => {
    setEditingSupplier(null);
    setIsModalVisible(true);
  };

  // モーダル閉じる
  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingSupplier(null);
  };

  // モーダル成功
  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  // テーブルカラム定義
  const columns: ResponsiveColumnType<Supplier>[] = [
    {
      title: `業者名`,
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (text, record) => (
        <NameLink
          name={text}
          onClick={() => navigate(`/app/suppliers/${record.id}`)}
        />
      ),
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (category) => (
        <CategoryTag
          category={category}
          linkPath="/app/suppliers"
        />
      ),
    },
    {
      title: '担当者',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 150,
      mobileRole: 'description',
      mobilePriority: 3,
      render: (text) => text ? (
          <Space>
            <UserOutlined />
            {text}
          </Space>
      ) : '-',
    },
    {
      title: '電話番号',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      mobileRole: 'description',
      mobilePriority: 4,
      render: (text) => text ? (
          <Space>
            <PhoneOutlined />
            {text}
          </Space>
      ) : '-',
    },
    {
      title: 'メール',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      mobileRole: 'hidden',
      render: (text) => text ? (
          <Space>
            <MailOutlined />
            {text}
          </Space>
      ) : '-',
    },
    {
      title: '住所',
      key: 'address',
      width: 250,
      mobileRole: 'hidden',
      render: (_, record) => {
        if (!record.address && !record.postalCode) return '-';
        return (
          <div>
            {record.postalCode && <div><Text type="secondary">〒{record.postalCode}</Text></div>}
            {record.address && <div>{record.address}</div>}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 40,
      align: 'center',
      fixed: 'right',
      mobileRole: 'hidden',
      render: (_, record) => (
        <ListActionsDropdown
          onDetail={() => navigate(`/app/suppliers/${record.id}`)}
          onEdit={() => handleEdit(record)}
          editPermission="manage"
        />
      ),
    },
  ];

  return (
    <PageContainer>
      {messageContextHolder}

      <PageHeader
        title="業者管理"
        subtitle="部品発注先や設備メーカーなどの業者情報を管理します"
        extra={
          <Space>
            <PermissionButton
              icon={<SettingOutlined />}
              onClick={() => navigate('/app/categories#supplier')}
              title="業者カテゴリ管理"
              requiredPermission="manage"
            >
              カテゴリ管理
            </PermissionButton>
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={handleCreate}
              requiredPermission="manage"
            >
              新規業者登録
            </PermissionButton>
          </Space>
        }
      />

      {/* 空状態UI: 業者が0件の場合 - ヘッダー直下に機能説明カードを表示 */}
      {suppliers.length === 0 && !isLoading && !searchText && !selectedCategory && (
        <Card
          style={{
            marginBottom: 16,
            background: token.colorInfoBg,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <ShopOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                業者を登録しましょう
              </Title>
              <Text type="secondary">
                業者を登録すると、以下の機能が利用できます。
              </Text>
            </Col>
            <Col>
              <PermissionButton type="primary" onClick={handleCreate} requiredPermission="manage">
                業者を登録する
              </PermissionButton>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <ToolOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                  <div>
                    <Text strong>設備管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      設備にメーカーや販売店を紐づけ
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>部品発注</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      発注先として業者を選択可能
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <ContactsOutlined style={{ fontSize: 20, color: token.purple }} />
                  <div>
                    <Text strong>連絡先管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      担当者情報を一元管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title={`登録業者数`}
              value={total}
              suffix="社"
            />
          </Card>
        </Col>
      </Row>

      {/* 検索・フィルタ */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              placeholder={`業者名、担当者名で検索`}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.field}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  field: value as SupplierListSortField,
                }))
              }
              data-testid="suppliers-sort-field-select"
            >
              <Select.Option value="name">業者名</Select.Option>
              <Select.Option value="contactPerson">担当者</Select.Option>
              <Select.Option value="updatedAt">更新日</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.order}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  order: value as SortPreference<SupplierListSortField>['order'],
                }))
              }
              data-testid="suppliers-sort-order-select"
            >
              <Select.Option value="asc">{getSortOrderLabel(sortPreference.field, 'asc')}</Select.Option>
              <Select.Option value="desc">{getSortOrderLabel(sortPreference.field, 'desc')}</Select.Option>
            </Select>
          </Col>
        </Row>

        {/* カテゴリタグクラウドフィルタ */}
        <CategoryTagCloudFilter
          categories={supplierCategories.map(cat => ({
            id: cat.id,
            categoryLabel: cat.categoryLabel,
            color: cat.color,
            icon: cat.icon,
            count: suppliers?.filter(s => s.supplierCategoryId === cat.id).length || 0,
          }))}
          selectedCategory={selectedCategory}
          onCategoryChange={(categoryId) => setSelectedCategory(categoryId)}
          loading={!supplierCategories.length && isLoading}
          showCounts={true}
          maxVisibleTags={15}
        />
      </Card>

      {/* 業者一覧テーブル */}
      <Card>
        {suppliers.length === 0 && !isLoading && !searchText && !selectedCategory ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Text type="secondary">上のカードから業者を登録してください</Text>
          </div>
        ) : (
          <ResponsiveTable<Supplier>
            columns={columns}
            dataSource={suppliers}
            loading={isLoading}
            rowKey="id"
            scrollX={1200}
            mobileCardConfig={{
              titleField: (record) => (
                <a
                  onClick={() => navigate(`/app/suppliers/${record.id}`)}
                  style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {record.name}
                </a>
              ),
              actions: (record) => (
                <ListActionsDropdown
                  onDetail={() => navigate(`/app/suppliers/${record.id}`)}
                  onEdit={() => handleEdit(record)}
                  editPermission="manage"
                />
              ),
              onClick: (record) => navigate(`/app/suppliers/${record.id}`),
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: false,
              showTotal: (total) => `全 ${total} 社`,
              onChange: (page) => setCurrentPage(page),
            }}
          />
        )}
      </Card>

      {/* 業者フォームモーダル */}
      <SupplierFormModal
        open={isModalVisible}
        supplier={editingSupplier}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </PageContainer>
  );
}
